import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  affectationApi,
  autoAffectationApi,
  createNewSubscriptionApi,
  searchClotureSubscriptionsApi,
  searchEnProcessSubscriptionsApi,
  searchNouvelSubscriptionsApi,
  searchPendingSubscriptionsApi,
  getSubscriptionByReferenceApi,
  acceptSubscriptionApi,
  rejectSubscriptionApi,
  startWorkflowApi,
  validateSubscriptionApi,
  getSubscriptionComplementApi,
  getSubscriptionHistoryApi,
} from "../apis/subscriptionApi";

export const useSearchNouvelSubscriptions = (
  body = {},
  params = { page: 0, pageSize: 5 }
) => {
  const bodyHash = JSON.stringify(body);
  return useQuery({
    queryKey: [
      "search-nouvel-subscriptions",
      bodyHash,
      params.page,
      params.pageSize,
    ],
    queryFn: () => searchNouvelSubscriptionsApi(body, params),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useSearchPendingSubscriptions = (
  body = {},
  params = { page: 0, pageSize: 5 }
) => {
  const bodyHash = JSON.stringify(body);
  return useQuery({
    queryKey: [
      "search-pending-subscriptions",
      bodyHash,
      params.page,
      params.pageSize,
    ],
    queryFn: () => searchPendingSubscriptionsApi(body, params),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useSearchClotureSubscriptions = (
  body = {},
  params = { page: 0, pageSize: 5 }
) => {
  const bodyHash = JSON.stringify(body);
  return useQuery({
    queryKey: [
      "search-cloture-subscriptions",
      bodyHash,
      params.page,
      params.pageSize,
    ],
    queryFn: () => searchClotureSubscriptionsApi(body, params),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useSearchEnProcessSubscriptions = (
  body = {},
  params = { page: 0, pageSize: 5 }
) => {
  const bodyHash = JSON.stringify(body);
  return useQuery({
    queryKey: [
      "search-en-process-subscriptions",
      bodyHash,
      params.page,
      params.pageSize,
    ],
    queryFn: () => searchEnProcessSubscriptionsApi(body, params),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useDepositNewSubscription = () => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["deposit_new_subscription"],
    mutationFn: async () => {
      const response = await createNewSubscriptionApi();
      return response;
    },
    onSuccess() {
      qClient.invalidateQueries(["current_manufacture"]);
    },
  });
};

export const useAffectation = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["affectation"],
    mutationFn: async (body) => {
      return affectationApi(body);
    },
    onSuccess() {
      qClient.invalidateQueries(["search-nouvel-subscriptions"]);
      onSuccess?.();
    },
  });
};

export const useAutoAffectation = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["auto_affectation"],
    mutationFn: async (subscriptionId) => {
      return autoAffectationApi(subscriptionId);
    },
    onSuccess() {
      qClient.invalidateQueries(["search-nouvel-subscriptions"]);
      onSuccess?.();
    },
  });
};

export const useSubscriptionDetails = (reference) => {
  return useQuery({
    queryKey: ["subscription_details", reference],
    queryFn: () => getSubscriptionByReferenceApi(reference),
    initialData: undefined,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useAcceptSubscription = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["accept_subscription"],
    mutationFn: async (subscriptionId) => {
      return acceptSubscriptionApi(subscriptionId);
    },
    onSuccess() {
      qClient.invalidateQueries(["subscription_details", "search-en-process-subscriptions"]);
      onSuccess?.();
    },
  });
};

export const useStartWorkflow = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["start_workflow"],
    mutationFn: async (subscriptionId) => {
      return startWorkflowApi(subscriptionId);
    },
    onSuccess() {
      qClient.invalidateQueries(["subscription_details", "search-pending-subscriptions"]);
      onSuccess?.();
    },
  });
};

export const useValidateSubscription = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["validate_subscription"],
    mutationFn: async (subscriptionId) => {
      return validateSubscriptionApi(subscriptionId);
    },
    onSuccess() {
      qClient.invalidateQueries(["search-en-process-subscriptions"]);
      onSuccess?.();
    },
  });
};

export const useRejectSubscription = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["reject_subscription"],
    mutationFn: async (body) => {
      return rejectSubscriptionApi(body);
    },
    onSuccess() {
      qClient.invalidateQueries(["subscription_details", "search-en-process-subscriptions"]);
      onSuccess?.();
    },
  });
};

export const useGetSubscriptionComplement = () => {
  return useQuery({
    queryKey: ["get-subscription-complement"],
    queryFn: () => getSubscriptionComplementApi(),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useSubscriptionHistory = (subscriptionId) => {
  return useQuery({
    queryKey: ["subscription_history", subscriptionId],
    queryFn: () => getSubscriptionHistoryApi(subscriptionId),
    enabled: !!subscriptionId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

